import React, { createContext, useContext, useState, useEffect } from "react";

const SidebarContext = createContext();

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider = ({ children }) => {
  // Initialize collapsed state based on screen size
  // Default to collapsed on tablet (768px - 1023px), expanded on desktop
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      const width = window.innerWidth;
      return width >= 768 && width < 1024; // Collapsed by default on tablet
    }
    return false;
  });

  // Update collapsed state when window is resized
  useEffect(() => {
    const handleResize = () => {
      try {
        const width = window.innerWidth;
        // On tablet, ensure it's collapsed by default
        if (width >= 768 && width < 1024) {
          // Only auto-collapse if user hasn't manually expanded it
          // We'll use localStorage to remember user preference
          const userPreference = localStorage.getItem("sidebarCollapsed");
          if (userPreference === null) {
            setIsCollapsed(true);
          }
        }
      } catch (error) {
        console.error("Error handling resize:", error);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load user preference from localStorage on mount
  useEffect(() => {
    try {
      const savedPreference = localStorage.getItem("sidebarCollapsed");
      if (savedPreference !== null) {
        setIsCollapsed(savedPreference === "true");
      }
    } catch (error) {
      console.error("Error loading sidebar preference:", error);
    }
  }, []);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const newValue = !prev;
      // Save user preference to localStorage
      try {
        localStorage.setItem("sidebarCollapsed", String(newValue));
      } catch (error) {
        console.error("Error saving sidebar preference:", error);
      }
      return newValue;
    });
  };

  return (
    <SidebarContext.Provider
      value={{ isCollapsed, setIsCollapsed, toggleCollapse }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
